import { Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  renderDocumentA4Html,
  renderReceiptDocumentHtml,
  renderText,
  type PrintDocument,
} from '@aeris/printing'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Select,
} from '@aeris/ui'

import { printHtmlDocument } from '../lib/label-print.ts'

/**
 * Um documento de `@aeris/printing` na tela e no papel (F135).
 *
 * O extrato e o recibo da baixa saem por aqui: prévia em texto e a escolha do
 * papel — bobina de 58 mm ou 80 mm, como o recibo de venda, ou folha A4, para o
 * extrato que vai num envelope ou anexado.
 */

type Paper = '32' | '48' | 'a4'

const PAPER_LABELS: Record<Paper, string> = {
  '32': 'Bobina 58 mm',
  '48': 'Bobina 80 mm',
  a4: 'Folha A4',
}

export const PrintDocumentDialog = ({
  document,
  description,
  onClose,
  initialPaper = '48',
  papers,
}: {
  document: PrintDocument
  description?: string
  onClose: () => void
  initialPaper?: Paper
  /**
   * Os papéis que este documento aceita (F170). Ausente, os três.
   *
   * A grade de tamanhos é uma tabela larga: na bobina ela sai cortada, e
   * oferecer a bobina seria oferecer um papel ilegível.
   */
  papers?: readonly Paper[]
}): ReactNode => {
  const offered = papers ?? (Object.keys(PAPER_LABELS) as Paper[])
  const [paper, setPaper] = useState<Paper>(
    offered.includes(initialPaper) ? initialPaper : (offered[0] ?? 'a4')
  )
  const width = paper === '32' ? 32 : 48
  const text = renderText(document, width)

  const print = (): void => {
    const html =
      paper === 'a4'
        ? renderDocumentA4Html(document)
        : renderReceiptDocumentHtml(document, width)
    printHtmlDocument(html).catch(() => {
      window.print()
    })
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={document.title}
        {...(description === undefined ? {} : { description })}
      >
        <div className="flex flex-col gap-3">
          <Field label="Papel">
            {(field) => (
              <Select
                {...field}
                value={paper}
                onChange={(event) => {
                  setPaper(event.target.value as Paper)
                }}
              >
                {offered.map((key) => (
                  <option key={key} value={key}>
                    {PAPER_LABELS[key]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div className="max-h-96 overflow-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-3">
            <pre
              aria-label={`Prévia: ${document.title}`}
              className="font-(--font-mono) text-(length:--text-xs) leading-snug text-(--text-primary)"
            >
              {text}
            </pre>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={print}
          >
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
